/**
 * W-Sidecart JavaScript
 * Handles cart drawer functionality, quantity updates, cross-sell, and Swiper integration
 */

class WSidecart {
  constructor() {
    this.sidecart = document.querySelector('[data-sidecart]');
    if (!this.sidecart) return;

    this.overlay = this.sidecart.querySelector('[data-sidecart-overlay]');
    this.panel = this.sidecart.querySelector('.sidecart__panel');
    this.content = this.sidecart.querySelector('[data-sidecart-content]');
    this.crosssellContainer = this.sidecart.querySelector('[data-sidecart-crosssell]');

    this.config = window.sidecartConfig || {
      freeShippingThreshold: 6000,
      giftThreshold: 10000,
      crossSellCount: 4,
      routes: {
        cart: '/cart',
        cartAdd: '/cart/add.js',
        cartChange: '/cart/change.js',
        checkout: '/checkout',
        recommendations: '/recommendations/products'
      },
      moneyFormat: '{{amount}}€'
    };

    this.swiper = null;
    this.isLoading = false;
    this.lastProductIdForRecommendations = null;

    this.init();
  }

  init() {
    this.bindEvents();
    this.initSwiper();

    // Store initial product ID from DOM if available
    const recommendationsEl = this.sidecart.querySelector('[data-product-id]');
    if (recommendationsEl && recommendationsEl.dataset.productId) {
      this.lastProductIdForRecommendations = recommendationsEl.dataset.productId;
    }

    this.loadRecommendations();
  }

  bindEvents() {
    // Close button
    const closeBtn = this.sidecart.querySelector('[data-sidecart-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Quantity buttons
    this.sidecart.addEventListener('click', (e) => {
      const minusBtn = e.target.closest('[data-sidecart-quantity-minus]');
      const plusBtn = e.target.closest('[data-sidecart-quantity-plus]');
      const removeBtn = e.target.closest('[data-sidecart-remove]');
      const crosssellAddBtn = e.target.closest('[data-crosssell-add]');

      if (minusBtn) {
        e.preventDefault();
        const key = minusBtn.dataset.sidecartQuantityMinus;
        this.updateQuantity(key, -1);
      }

      if (plusBtn) {
        e.preventDefault();
        const key = plusBtn.dataset.sidecartQuantityPlus;
        this.updateQuantity(key, 1);
      }

      if (removeBtn) {
        e.preventDefault();
        const key = removeBtn.dataset.sidecartRemove;
        this.removeItem(key);
      }

      if (crosssellAddBtn) {
        e.preventDefault();
        const variantId = crosssellAddBtn.dataset.crosssellAdd;
        const productId = crosssellAddBtn.dataset.productId;
        this.addCrosssellItem(variantId, productId, crosssellAddBtn);
      }
    });

    // Cross-sell variant change
    this.sidecart.addEventListener('change', (e) => {
      const variantSelect = e.target.closest('[data-crosssell-variant]');
      if (variantSelect) {
        const productId = variantSelect.dataset.crosssellVariant;
        const addBtn = this.sidecart.querySelector(`[data-crosssell-add][data-product-id="${productId}"]`);
        if (addBtn) {
          addBtn.dataset.crosssellAdd = variantSelect.value;
        }
      }
    });

    // Listen for cart updates from other parts of the site
    document.addEventListener('cart:updated', () => this.refresh());

    // Listen for cart:item-added from pleiades.js
    document.addEventListener('cart:item-added', async () => {
      await this.refresh();
    });

    // Listen for cart-drawer:open to sync with existing theme behavior
    document.addEventListener('cart-drawer:open', () => {
      // Le sidecart est déjà ouvert via openCartDrawer dans pleiades.js
    });
  }

  isOpen() {
    return this.sidecart.getAttribute('aria-hidden') === 'false';
  }

  open() {
    this.sidecart.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('sidecart-open');
    document.body.classList.add('sidecart-open');

    // Focus trap
    this.panel.focus();
  }

  close() {
    this.sidecart.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('sidecart-open');
    document.body.classList.remove('sidecart-open');
  }

  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  setLoadingState(isLoading, loadingItem = null) {
    this.isLoading = isLoading;

    // Disable/enable all buttons in the sidecart
    const allButtons = this.sidecart.querySelectorAll('button, .button, .button--primary, .button--cta, .button--primary-small');
    allButtons.forEach(btn => {
      btn.disabled = isLoading;
    });

    // Also disable checkout link
    const checkoutLink = this.sidecart.querySelector('.sidecart__checkout');
    if (checkoutLink) {
      if (isLoading) {
        checkoutLink.style.pointerEvents = 'none';
        checkoutLink.style.opacity = '0.5';
      } else {
        checkoutLink.style.pointerEvents = '';
        checkoutLink.style.opacity = '';
      }
    }

    // Set loading state on specific item
    if (loadingItem) {
      if (isLoading) {
        loadingItem.setAttribute('data-loading', 'true');
      } else {
        loadingItem.removeAttribute('data-loading');
      }
    }
  }

  async updateQuantity(key, change) {
    if (this.isLoading) return;

    const item = this.sidecart.querySelector(`[data-sidecart-item][data-key="${key}"]`);
    if (!item) return;

    const quantityValue = item.querySelector(`[data-sidecart-quantity-value="${key}"]`);
    const currentQty = parseInt(quantityValue.textContent, 10);
    const newQty = Math.max(0, currentQty + change);

    if (newQty === 0) {
      this.removeItem(key);
      return;
    }

    this.setLoadingState(true, item);

    try {
      const response = await fetch(this.config.routes.cartChange, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: newQty
        })
      });

      if (response.ok) {
        await this.refresh();
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    } finally {
      this.setLoadingState(false, item);
    }
  }

  async removeItem(key) {
    if (this.isLoading) return;

    const item = this.sidecart.querySelector(`[data-sidecart-item][data-key="${key}"]`);
    if (!item) return;

    this.setLoadingState(true, item);

    try {
      const response = await fetch(this.config.routes.cartChange, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      });

      if (response.ok) {
        await this.refresh();
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
    } catch (error) {
      console.error('Error removing item:', error);
    } finally {
      this.setLoadingState(false);
    }
  }

  async addCrosssellItem(variantId, productId, button) {
    if (this.isLoading) return;

    this.setLoadingState(true);
    const originalText = button.textContent;
    button.textContent = '...';

    try {
      const response = await fetch(this.config.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: 1
          }]
        })
      });

      if (response.ok) {
        await this.refresh();
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.description || 'Ce produit est indisponible';
        button.textContent = errorMsg;
        setTimeout(() => { button.textContent = originalText; }, 2000);
        return;
      }
    } catch (error) {
      console.error('Error adding crosssell item:', error);
    } finally {
      this.setLoadingState(false);
      if (button.textContent === '...') {
        button.textContent = originalText;
      }
    }
  }

  async refresh(skipGifts = false) {
    try {
      // Fetch updated cart HTML via section rendering
      const response = await fetch(`/?sections=w-sidecart-content`);

      if (response.ok) {
        const data = await response.json();
        if (data['w-sidecart-content']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data['w-sidecart-content'], 'text/html');

          // Update content
          const newContent = doc.querySelector('[data-sidecart-content]');
          if (newContent && this.content) {
            this.content.innerHTML = newContent.innerHTML;
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing sidecart:', error);
    }

    // Always update from cart data for accurate totals
    await this.refreshFromCartData(skipGifts);
    this.loadRecommendations();
  }

  async refreshFromCartData(skipGifts = false) {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Calculate total without gift products (to avoid circular dependency)
      const totalWithoutGifts = this.calculateTotalWithoutGifts(cart);

      // Update count
      const countEl = this.sidecart.querySelector('[data-sidecart-count]');
      if (countEl) {
        countEl.textContent = cart.item_count;
      }

      // Update header cart count
      const headerCounts = document.querySelectorAll('[data-cart-count]');
      headerCounts.forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });

      // Update total
      const totalEl = this.sidecart.querySelector('[data-sidecart-total]');
      if (totalEl) {
        totalEl.textContent = this.formatMoney(cart.total_price);
      }

      // Update progress bars (use total without gifts to calculate thresholds)
      this.updateProgressBars(totalWithoutGifts);

      // Handle gift products based on thresholds (skip if called from gift handling to avoid loop)
      if (!skipGifts) {
        const giftChanged = await this.handleGiftProducts(cart, totalWithoutGifts);
        if (giftChanged) {
          await this.refresh(true);
          return;
        }
      }

      // Show/hide footer
      const footer = this.sidecart.querySelector('[data-sidecart-footer]');
      if (footer) {
        footer.style.display = cart.item_count > 0 ? '' : 'none';
      }

      // Show/hide empty state
      this.updateEmptyState(cart.item_count);

    } catch (error) {
      console.error('Error fetching cart data:', error);
    }
  }

  /**
   * Calculate cart total excluding gift products
   */
  calculateTotalWithoutGifts(cart) {
    let total = 0;
    cart.items.forEach(item => {
      // Exclude items with _gift property
      const isGift = item.properties && item.properties._gift;
      if (!isGift) {
        total += item.final_line_price;
      }
    });
    return total;
  }

  /**
   * Handle automatic gift product add/remove based on thresholds
   */
  async handleGiftProducts(cart, totalWithoutGifts) {
    // Prevent concurrent gift handling
    if (this.isHandlingGifts) return false;
    this.isHandlingGifts = true;

    let changed = false;

    try {
      const steps = this.config.steps || [];

      for (const step of steps) {
        if (!step.giftProduct) continue;

        const giftVariantId = step.giftProduct.variantId;
        const thresholdReached = totalWithoutGifts >= step.threshold;

        // Check if gift is already in cart
        const giftInCart = cart.items.find(item =>
          item.variant_id === giftVariantId &&
          item.properties &&
          item.properties._gift === step.id
        );

        if (thresholdReached && !giftInCart) {
          await this.addGiftProduct(step);
          changed = true;
        } else if (!thresholdReached && giftInCart) {
          await this.removeGiftProduct(giftInCart.key);
          changed = true;
        }
      }
    } finally {
      this.isHandlingGifts = false;
    }

    return changed;
  }

  /**
   * Add a gift product to cart
   */
  async addGiftProduct(step) {
    try {
      await fetch(this.config.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: step.giftProduct.variantId,
            quantity: 1,
            properties: {
              _gift: step.id,
              _gift_label: step.label
            }
          }]
        })
      });
    } catch (error) {
      console.error('Error adding gift product:', error);
    }
  }

  /**
   * Remove a gift product from cart
   */
  async removeGiftProduct(key) {
    try {
      await fetch(this.config.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      });
    } catch (error) {
      console.error('Error removing gift product:', error);
    }
  }

  updateProgressBars(totalPrice) {
    const steps = this.config.steps || [];
    if (steps.length === 0) return;

    const ANIMATION_DURATION = 500; // 0.5s in ms

    // Sort steps by threshold
    const sortedSteps = [...steps].sort((a, b) => a.threshold - b.threshold);

    // Find active bar index (the one currently filling/emptying)
    let activeBarIndex = 0;
    for (let i = 0; i < sortedSteps.length; i++) {
      if (totalPrice < sortedSteps[i].threshold) {
        activeBarIndex = i;
        break;
      }
      if (i === sortedSteps.length - 1) {
        activeBarIndex = sortedSteps.length; // All complete
      }
    }

    // Detect if we crossed a threshold
    const previousActiveIndex = this.previousActiveBarIndex ?? activeBarIndex;
    const thresholdCrossed = activeBarIndex !== previousActiveIndex;
    const isGoingUp = activeBarIndex > previousActiveIndex;
    this.previousActiveBarIndex = activeBarIndex;

    // Clear any pending timeouts
    if (this.progressTimeouts) {
      this.progressTimeouts.forEach(t => clearTimeout(t));
    }
    this.progressTimeouts = [];

    // Calculate progress for each step
    let allReached = true;
    let firstUnreachedStep = null;

    sortedSteps.forEach((step, index) => {
      const reached = totalPrice >= step.threshold;
      if (!reached && !firstUnreachedStep) {
        allReached = false;
        firstUnreachedStep = step;
      }

      // Calculate the start and end thresholds for this bar
      const startThreshold = index === 0 ? 0 : sortedSteps[index - 1].threshold;
      const endThreshold = step.threshold;
      const range = endThreshold - startThreshold;

      // Update fill for this step
      const fill = this.sidecart.querySelector(`[data-sidecart-fill="${step.id}"]`);
      if (fill) {
        let stepProgress = 0;

        if (index < activeBarIndex) {
          stepProgress = 100;
        } else if (index > activeBarIndex) {
          stepProgress = 0;
        } else {
          const current = totalPrice - startThreshold;
          stepProgress = Math.min(100, Math.max(0, (current / range) * 100));
        }

        // Only apply delay when crossing a threshold
        let delay = 0;
        if (thresholdCrossed) {
          if (isGoingUp) {
            // Going up: lower bars fill first, then higher bars follow
            // Bars at index > previousActiveIndex wait for previous ones to complete
            if (index > previousActiveIndex) {
              delay = (index - previousActiveIndex) * ANIMATION_DURATION;
            }
          } else {
            // Going down: higher bars empty first, then lower bars follow
            // Highest bar (index = length-1) empties immediately
            // Lower bars wait for higher ones to finish
            const highestIndex = sortedSteps.length - 1;
            delay = (highestIndex - index) * ANIMATION_DURATION;
          }
        }

        const timeout = setTimeout(() => {
          fill.style.transform = `scaleX(${stepProgress / 100})`;
        }, delay);
        this.progressTimeouts.push(timeout);
      }

      // Update label
      const label = this.sidecart.querySelector(`[data-sidecart-label="${step.id}"]`);
      if (label) {
        label.classList.toggle('sidecart__progress-label--active', reached);
      }
    });

    // Update text
    const progressText = this.sidecart.querySelector('[data-sidecart-progress-text]');
    if (progressText) {
      if (allReached) {
        progressText.textContent = this.config.completionMessage;
      } else if (firstUnreachedStep) {
        const remaining = Math.max(0, firstUnreachedStep.threshold - totalPrice);
        progressText.textContent = firstUnreachedStep.message.replace('{amount}', this.formatMoney(remaining));
      }
    }
  }

  updateEmptyState(itemCount) {
    const productsEl = this.sidecart.querySelector('[data-sidecart-products]');
    let emptyEl = this.sidecart.querySelector('[data-sidecart-empty]');

    const crosssellTitle = this.sidecart.querySelector('[data-crosssell-title]');

    if (itemCount === 0) {
      if (productsEl) {
        productsEl.style.display = 'none';
      }
      if (!emptyEl && this.content) {
        emptyEl = document.createElement('div');
        emptyEl.className = 'sidecart__empty';
        emptyEl.setAttribute('data-sidecart-empty', '');
        emptyEl.innerHTML = `
          <p class="sidecart__empty-text p-extra-large text-center">Votre panier est vide</p>
          <a href="/collections/all" class="button button--primary">Decouvrir nos produits</a>
        `;
        this.content.appendChild(emptyEl);
      }
      if (emptyEl) {
        emptyEl.style.display = '';
      }
      if (crosssellTitle) {
        crosssellTitle.textContent = crosssellTitle.getAttribute('data-crosssell-title-empty');
      }
    } else {
      if (productsEl) {
        productsEl.style.display = '';
      }
      if (emptyEl) {
        emptyEl.style.display = 'none';
      }
      if (crosssellTitle) {
        crosssellTitle.textContent = crosssellTitle.getAttribute('data-crosssell-title');
      }
    }
  }

  formatMoney(cents) {
    // Utiliser la fonction globale formatMoney du thème si disponible
    if (typeof formatMoney === 'function') {
      return formatMoney(cents, this.config.moneyFormat);
    }
    // Fallback simple
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  initSwiper() {
    const swiperEl = this.sidecart.querySelector('[data-sidecart-swiper]');
    if (!swiperEl || typeof Swiper === 'undefined') return;

    // Détruire l'ancien swiper si existe
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    const paginationEl = this.sidecart.querySelector('.sidecart__crosssell-pagination');

    this.swiper = new Swiper(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 12,
      pagination: {
        el: paginationEl,
        clickable: true
      }
    });
  }

  async loadRecommendations() {
    const wrapper = this.sidecart.querySelector('.sidecart__crosssell-slider .swiper-wrapper');
    if (!wrapper) return;

    try {
      // Fetch current cart to get product IDs
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      let productIdToUse = null;

      if (cart.items.length > 0) {
        // Use first product ID for recommendations
        productIdToUse = cart.items[0].product_id;
        // Store it for later use when cart becomes empty
        this.lastProductIdForRecommendations = productIdToUse;
      } else if (this.lastProductIdForRecommendations) {
        // Cart is empty but we have a previous product ID, use it
        productIdToUse = this.lastProductIdForRecommendations;
      } else {
        // No items and no previous product ID - keep current recommendations if any
        return;
      }

      const firstProductId = productIdToUse;
      const url = `${this.config.routes.recommendations}?section_id=w-sidecart-recommendations&product_id=${firstProductId}&limit=${this.config.crossSellCount}`;

      const response = await fetch(url);
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const slides = doc.querySelectorAll('.swiper-slide');

        // Vider le wrapper
        wrapper.innerHTML = '';

        // Ajouter les slides
        if (slides.length > 0) {
          slides.forEach(slide => {
            wrapper.appendChild(slide.cloneNode(true));
          });
        }

        // Réinitialiser Swiper après un court délai pour laisser le DOM se mettre à jour
        setTimeout(() => {
          this.initSwiper();
        }, 100);
      }
    } catch (error) {
      console.error('Error loading recommendations:', error);
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  window.wSidecart = new WSidecart();
});

// Global function to open sidecart
window.openSidecart = function() {
  if (window.wSidecart) {
    window.wSidecart.open();
    window.wSidecart.refresh();
  } else {
    // Si pas encore initialisé, ouvrir directement via DOM
    const sidecart = document.querySelector('[data-sidecart]');
    if (sidecart) {
      sidecart.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('sidecart-open');
      document.body.classList.add('sidecart-open');
    }
  }
};

// Global function to close sidecart
window.closeSidecart = function() {
  if (window.wSidecart) {
    window.wSidecart.close();
  } else {
    const sidecart = document.querySelector('[data-sidecart]');
    if (sidecart) {
      sidecart.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('sidecart-open');
      document.body.classList.remove('sidecart-open');
    }
  }
};

// Global function to toggle sidecart
window.toggleSidecart = function() {
  if (window.wSidecart) {
    window.wSidecart.toggle();
  } else {
    const sidecart = document.querySelector('[data-sidecart]');
    if (sidecart) {
      const isOpen = sidecart.getAttribute('aria-hidden') === 'false';
      sidecart.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
      document.documentElement.classList.toggle('sidecart-open', !isOpen);
      document.body.classList.toggle('sidecart-open', !isOpen);
    }
  }
};
